// Generates a 1080×1920 PNG Season Recap social card (Instagram Story format).
// Auto-composites:
//   - Player name + team + nationality
//   - Trophies won this season (league rank, cup, UCL/UEL/UECL/CWC badges)
//   - Headline stat (goals/assists/rating)
//   - Best competition MVP / TOTY spot if made
//
// Pure canvas — no external deps. Returns a data URL and triggers download.

const teamHue = (name = '') => {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return h % 360;
};

const initials = (name = '') => {
  const s = name.replace(/[^A-Za-z\s]/g, '').split(/\s+/).filter(Boolean);
  if (s.length === 1) return s[0].slice(0, 2).toUpperCase();
  return (s[0][0] + s[s.length - 1][0]).toUpperCase();
};

const roundRect = (ctx, x, y, w, h, r) => {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
};

const drawCrest = (ctx, name, x, y, size) => {
  const hue = teamHue(name);
  ctx.save();
  ctx.translate(x, y);
  const grad = ctx.createLinearGradient(0, -size / 2, 0, size / 2);
  grad.addColorStop(0, `hsl(${hue}, 78%, 52%)`);
  grad.addColorStop(1, `hsl(${hue}, 75%, 22%)`);
  ctx.fillStyle = grad;
  ctx.strokeStyle = `hsl(${hue}, 90%, 75%)`;
  ctx.lineWidth = size * 0.04;
  const s = size / 2;
  ctx.beginPath();
  ctx.moveTo(0, -s);
  ctx.lineTo(s * 0.85, -s * 0.7);
  ctx.lineTo(s * 0.85, s * 0.1);
  ctx.quadraticCurveTo(s * 0.85, s * 0.7, 0, s);
  ctx.quadraticCurveTo(-s * 0.85, s * 0.7, -s * 0.85, s * 0.1);
  ctx.lineTo(-s * 0.85, -s * 0.7);
  ctx.closePath();
  ctx.fill();
  ctx.stroke();
  ctx.fillStyle = '#FFF';
  ctx.font = `900 ${size * 0.44}px 'Bebas Neue', 'Barlow Condensed', Impact, sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(initials(name), 0, size * 0.04);
  ctx.restore();
};

/**
 * @param {Object} opts
 * @param {Object} opts.player         - { name, nationality, flag, position, age, id }
 * @param {Object} opts.team           - { name }
 * @param {number} opts.season
 * @param {number} opts.year
 * @param {number} opts.finalRank      - League rank 1-20
 * @param {Array}  opts.trophies       - [{ label, color, icon? }]
 * @param {Object} opts.stats          - { matches, goals, assists, avgRating }
 * @param {Object} [opts.mvpComps]     - { [compKey]: true } comps where player is MVP
 * @param {Object} [opts.totyComps]    - { [compKey]: true } comps where player made TOTY
 * @returns {Promise<string>} data URL
 */
export const generateSeasonRecapStory = async ({
  player, team, season, year, finalRank,
  trophies = [], stats = {}, mvpComps = {}, totyComps = {},
}) => {
  const W = 1080, H = 1920;
  const canvas = document.createElement('canvas');
  canvas.width = W; canvas.height = H;
  const ctx = canvas.getContext('2d');

  const teamH = teamHue(team.name);

  // ===== Background — deep radial + team color halos =====
  const bg = ctx.createLinearGradient(0, 0, 0, H);
  bg.addColorStop(0, '#050911');
  bg.addColorStop(0.5, '#0A1627');
  bg.addColorStop(1, '#030509');
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, W, H);

  // Massive team-color halo
  ctx.fillStyle = `hsla(${teamH}, 80%, 50%, 0.25)`;
  ctx.beginPath(); ctx.arc(W / 2, 780, 680, 0, Math.PI * 2); ctx.fill();
  ctx.fillStyle = 'rgba(212, 169, 51, 0.12)';
  ctx.beginPath(); ctx.arc(W / 2, 1500, 600, 0, Math.PI * 2); ctx.fill();

  // Diagonal stripes for broadcast feel
  ctx.save();
  ctx.translate(W / 2, H / 2);
  ctx.rotate(-Math.PI / 14);
  ctx.translate(-W / 2, -H / 2);
  for (let i = 0; i < 22; i++) {
    ctx.fillStyle = i % 2 === 0 ? 'rgba(255,255,255,0.01)' : 'rgba(212,169,51,0.015)';
    ctx.fillRect(-200 + i * 70, -200, 36, H + 400);
  }
  ctx.restore();

  // Top gold bar
  ctx.fillStyle = '#D4A933';
  ctx.fillRect(0, 0, W, 14);

  // Header block
  ctx.fillStyle = '#0A0A0C';
  ctx.fillRect(0, 14, W, 88);
  ctx.fillStyle = '#D4A933';
  ctx.font = "900 28px 'JetBrains Mono', monospace";
  ctx.textAlign = 'left';
  ctx.fillText('JWFL', 50, 70);
  ctx.fillStyle = '#FFFFFF';
  ctx.font = "700 22px 'JetBrains Mono', monospace";
  ctx.fillText('· SEASON RECAP', 160, 70);
  ctx.textAlign = 'right';
  ctx.fillStyle = '#7A8FAB';
  ctx.font = "600 18px 'JetBrains Mono', monospace";
  ctx.fillText(`S${season} · ${year}`, W - 50, 70);

  // ===== Player block =====
  // Crest big
  drawCrest(ctx, team.name, W / 2, 310, 220);

  // Flag (optional)
  if (player.flag) {
    ctx.font = "70px 'Noto Color Emoji', sans-serif";
    ctx.textAlign = 'center';
    ctx.fillText(player.flag, W / 2, 520);
  }

  // Player name — huge
  ctx.fillStyle = '#FFFFFF';
  ctx.textAlign = 'center';
  ctx.font = "900 108px 'Bebas Neue', 'Barlow Condensed', Impact, sans-serif";
  const playerName = (player.name || 'PLAYER').toUpperCase();
  ctx.fillText(playerName, W / 2, 620);

  // Team + position
  ctx.fillStyle = '#D4A933';
  ctx.font = "700 30px 'Barlow Condensed', Impact, sans-serif";
  ctx.fillText(`${player.position || ''} · ${team.name.toUpperCase()}`, W / 2, 680);

  // League final rank chip
  const rankLabel = finalRank ? (finalRank === 1 ? '🏆 CHAMPIONS' : finalRank <= 2 ? `${finalRank === 2 ? '2ND' : '1ST'} · CWC + ASIAN CUP` : finalRank <= 4 ? `#${finalRank} · ASIAN CUP SPOT` : `#${finalRank}`) : '';
  if (rankLabel) {
    ctx.fillStyle = finalRank === 1 ? '#FFD700' : finalRank <= 4 ? '#32D74B' : finalRank <= 6 ? '#FF6B00' : finalRank <= 8 ? '#32D74B' : '#7A8FAB';
    ctx.font = "900 38px 'JetBrains Mono', monospace";
    ctx.fillText(rankLabel, W / 2, 740);
  }

  // ===== Stat strip =====
  const statY = 800;
  const statCell = (x, label, value, color = '#FFFFFF') => {
    ctx.fillStyle = color;
    ctx.textAlign = 'center';
    ctx.font = "900 92px 'Bebas Neue', 'Barlow Condensed', Impact, sans-serif";
    ctx.fillText(String(value), x, statY + 90);
    ctx.fillStyle = '#7A8FAB';
    ctx.font = "700 18px 'JetBrains Mono', monospace";
    ctx.fillText(label.toUpperCase(), x, statY + 120);
  };
  statCell(W * 0.18, 'Apps', stats.matches || 0, '#4DA3FF');
  statCell(W * 0.38, 'Goals', stats.goals || 0, '#32D74B');
  statCell(W * 0.62, 'Assists', stats.assists || 0, '#D4A933');
  statCell(W * 0.82, 'Rating', (stats.avgRating || 0).toFixed(1), '#FFD700');

  // Decorative divider
  const grad = ctx.createLinearGradient(0, 950, W, 950);
  grad.addColorStop(0, 'rgba(212,169,51,0)');
  grad.addColorStop(0.5, '#D4A933');
  grad.addColorStop(1, 'rgba(212,169,51,0)');
  ctx.fillStyle = grad;
  ctx.fillRect(80, 980, W - 160, 2);

  // ===== Trophies =====
  ctx.fillStyle = '#D4A933';
  ctx.textAlign = 'center';
  ctx.font = "900 32px 'Bebas Neue', 'Barlow Condensed', Impact, sans-serif";
  ctx.fillText('SILVERWARE & HONOURS', W / 2, 1040);

  const trophyList = trophies.length ? trophies : [{ label: 'SEASON COMPLETED', color: '#7A8FAB' }];
  const tStart = 1090;
  const tH = 88;
  const maxTrophies = 5;
  trophyList.slice(0, maxTrophies).forEach((t, i) => {
    const y = tStart + i * (tH + 14);
    ctx.fillStyle = 'rgba(10, 22, 39, 0.9)';
    roundRect(ctx, 90, y, W - 180, tH, 10);
    ctx.fill();
    ctx.fillStyle = t.color || '#D4A933';
    ctx.fillRect(90, y, 8, tH);
    ctx.fillStyle = '#FFFFFF';
    ctx.textAlign = 'left';
    ctx.font = "900 36px 'Bebas Neue', 'Barlow Condensed', Impact, sans-serif";
    ctx.fillText(t.label.toUpperCase(), 130, y + 58);
    if (t.icon) {
      ctx.textAlign = 'right';
      ctx.font = "44px 'Noto Color Emoji', sans-serif";
      ctx.fillText(t.icon, W - 120, y + 58);
    }
  });

  // ===== MVP / TOTY badges =====
  const badgeY = 1620;
  const mvpList = Object.keys(mvpComps || {});
  const totyList = Object.keys(totyComps || {});
  if (mvpList.length || totyList.length) {
    ctx.fillStyle = '#FFD700';
    ctx.textAlign = 'center';
    ctx.font = "900 28px 'JetBrains Mono', monospace";
    ctx.fillText('★ INDIVIDUAL AWARDS ★', W / 2, badgeY);
    const all = [
      ...mvpList.map(k => ({ label: `${k.toUpperCase()} MVP`, color: '#FFD700' })),
      ...totyList.map(k => ({ label: `${k.toUpperCase()} TOTY`, color: '#32D74B' })),
    ];
    const badgeW = Math.min(260, (W - 200) / Math.max(1, all.length));
    all.slice(0, 4).forEach((b, i) => {
      const x = W / 2 - (all.length * badgeW) / 2 + i * badgeW;
      ctx.fillStyle = b.color;
      roundRect(ctx, x + 10, badgeY + 30, badgeW - 20, 60, 8);
      ctx.fill();
      ctx.fillStyle = '#000';
      ctx.font = "900 24px 'Bebas Neue', 'Barlow Condensed', Impact, sans-serif";
      ctx.fillText(b.label, x + badgeW / 2, badgeY + 68);
    });
  }

  // ===== Footer =====
  ctx.fillStyle = '#0A0A0C';
  ctx.fillRect(0, H - 100, W, 100);
  ctx.fillStyle = '#D4A933';
  ctx.fillRect(0, H - 12, W, 12);
  ctx.fillStyle = '#FFFFFF';
  ctx.textAlign = 'center';
  ctx.font = "900 30px 'Bebas Neue', 'Barlow Condensed', Impact, sans-serif";
  ctx.fillText(`SEASON ${season} · #JAPANWORLDFOOTBALLLEAGUE`, W / 2, H - 55);
  ctx.fillStyle = '#7A8FAB';
  ctx.font = "600 18px 'JetBrains Mono', monospace";
  ctx.fillText('ewfl · built for football', W / 2, H - 28);

  return canvas.toDataURL('image/png');
};

export const downloadSeasonRecap = async (opts) => {
  const url = await generateSeasonRecapStory(opts);
  const a = document.createElement('a');
  a.href = url;
  const safe = (s) => (s || 'player').replace(/[^A-Za-z0-9]/g, '_');
  a.download = `JWFL_Season${opts.season}_${safe(opts.player?.name)}.png`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  return url;
};

// Convert a data URL to a File instance suitable for Web Share API
const dataUrlToFile = async (dataUrl, filename) => {
  const res = await fetch(dataUrl);
  const blob = await res.blob();
  return new File([blob], filename, { type: blob.type || 'image/png' });
};

/**
 * Try to share the recap via the Web Share API (Instagram / Twitter / system sheet).
 * Falls back to a plain PNG download if unsupported.
 *
 * @returns {Promise<{shared: boolean, via: 'webshare' | 'download'}>}
 */
export const shareSeasonRecap = async (opts) => {
  const dataUrl = await generateSeasonRecapStory(opts);
  const safe = (s) => (s || 'player').replace(/[^A-Za-z0-9]/g, '_');
  const filename = `JWFL_Season${opts.season}_${safe(opts.player?.name)}.png`;
  const file = await dataUrlToFile(dataUrl, filename);

  const shareData = {
    files: [file],
    title: `JWFL · Season ${opts.season} Recap`,
    text: `${opts.player?.name || 'My'} — Season ${opts.season} recap · ${opts.trophies?.length || 0} trophies · ${opts.stats?.goals || 0} goals · ${opts.stats?.assists || 0} assists. #JapanWorldFootballLeague`,
  };

  if (typeof navigator !== 'undefined' && navigator.canShare && navigator.canShare(shareData)) {
    try {
      await navigator.share(shareData);
      return { shared: true, via: 'webshare' };
    } catch (err) {
      // User cancelled share sheet — don't fall back to download silently
      if (err?.name === 'AbortError') return { shared: false, via: 'webshare' };
    }
  }

  // Fallback — plain download
  const a = document.createElement('a');
  a.href = dataUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  return { shared: true, via: 'download' };
};
